import { useEffect } from 'react'
import { useAtomValue } from 'jotai'
import { useFooterSlotClaimant } from '../../atoms'
import { meetingNotepadModeAtom } from './atoms'
import { meetingNotepadDraggingAtom, useMeetingNotepadHeights, NOTEPAD_INSET_PX } from './notepad-height'
import { MeetingViewNotepad } from './nested/notepad'

/**
 * Claims the footer's bottom slot for this meeting's notepad while it's open,
 * and gives it back when it isn't.
 *
 * Its own file rather than sitting beside the toggle and the content: the claim
 * names the component that fills the slot, and the notepad reads its content
 * from `use-meeting-notepad` — together in one module that would be a cycle.
 *
 * Called by the Meeting View and, while a meeting is live, by the floating
 * bar's meeting cluster — so the same meeting's notepad is reachable from Home
 * and the Action View too, not just from the meeting itself. The claim's own
 * teardown is what returns the chat input, so a mode that outlives its host is
 * inert rather than wrong: nothing claims the slot until a host holding that
 * key is back on screen. That inertness is why the mode is *not* cleared on
 * unmount — doing so would let whichever host unmounts first slam the notepad
 * shut on the one taking over mid-navigation.
 *
 * The handover between hosts is not a clean swap: the view router animates the
 * outgoing view out, so the Meeting View is still mounted (its cleanup still
 * pending) while the floating bar's cluster has already taken the slot over.
 * Releases are therefore scoped to the claim this host actually made, which is
 * what `useFooterSlotClaimant` guarantees.
 *
 * No key claims nothing: the `tabs` layout's Meeting View edits the notes in a
 * tab instead.
 */
export function useMeetingNotepadClaim(key: string | undefined) {
  const mode = useAtomValue(meetingNotepadModeAtom)
  const { claim, release } = useFooterSlotClaimant()
  const heights = useMeetingNotepadHeights()
  const isDragging = useAtomValue(meetingNotepadDraggingAtom)

  const size = key !== undefined && mode?.key === key ? mode.size : undefined
  const height = size === undefined ? undefined : heights[size]

  useEffect(() => {
    if (height === undefined) return
    claim({
      height: height + NOTEPAD_INSET_PX,
      component: MeetingViewNotepad,
      coversPanel: size === 'full',
      // The drag is already the animation; a morph on top of it reads as the
      // edge lagging the pointer by a quarter of a second.
      instant: isDragging,
    })
    return release
  }, [height, size, isDragging, claim, release])
}
